import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom"; // Importado para substituir o Link em escopo de string
import Footer from "../../components/Footer";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Swal from "sweetalert2";
import { api } from "../../axios/api";
import { numberFormat } from "../../config/Config";
import { Modal, Table, InputGroup, Form } from "react-bootstrap";
import { PlusCircle, Search, ChevronRight } from "lucide-react";

import MyDataTable from "../../components/DataTable";
import FaturaSucessoModal from "../../components/FaturaSucesso";

const DocumentosEmitidos = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;
  const navigate = useNavigate(); // Hook para navegação programática rápida

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("ALL");
  
  // Estados para os Modais
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Expor funções e rotas para o escopo window global
  useEffect(() => {
    window.handleOpenDetailsGlobal = (id) => {
      const doc = data.find(d => d.id === id);
      if (doc) {
        setSelectedDoc(doc);
        setShowDetailsModal(true);
      }
    };

    window.handleOpenGenerateGlobal = (id) => {
      const doc = data.find(d => d.id === id);
      if (doc) {
        setSelectedDoc(doc);
        setShowSuccessModal(true);
      }
    };

    // Função que substitui a ação de clique do <Link to> de forma nativa no React Router
    window.handleNavigateGlobal = (rota) => {
      navigate(rota);
    };

    return () => {
      delete window.handleOpenDetailsGlobal;
      delete window.handleOpenGenerateGlobal;
      delete window.handleNavigateGlobal;
    };
  }, [data, navigate]);

  useEffect(() => {
    if (empresaId) fetchDocumentos();
  }, [empresaId]);

  const fetchDocumentos = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/app/documentos/filter-data/query/?empresa_id=${empresaId}`);
      setData(response.data.data);
    } catch (error) {
      Swal.fire("Erro", "Não foi possível carregar os documentos.", "error");
    } finally {
      setLoading(false);
    }
  };

  const getDocLabel = (type) => {
    const labels = {
      FT: "FATURA",
      FR: "FATURA RECIBO",
      FP: "PRO-FORMA",
      NC: "NOTA DE CRÉDITO",
      ND: "NOTA DE DÉBITO",
      RG: "RECIBO",
    };
    return labels[type] || type;
  };

  const filteredData = data.filter(doc => {
    const matchesSearch = doc.numero?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         doc.Cliente?.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "ALL" || doc.doc_type === filterType;
    return matchesSearch && matchesType;
  });

  // 1. ESTRUTURA DE COLUNAS DO DATAGRID
  const columns = [
    { title: "TIPO", selector: "tipo_formatado", data: "tipo_formatado", sortable: true },
    { title: "Nº DOCUMENTO", selector: "numero", data: "numero", sortable: true },
    { title: "CLIENTE", selector: "cliente_nome", data: "cliente_nome", sortable: true },
    { title: "DATA EMISSÃO", selector: "data_formatada", data: "data_formatada", sortable: true },
    { title: "VALOR TOTAL", selector: "valor_formatado", data: "valor_formatado", sortable: true },
    { title: "ESTADO", selector: "estado_formatado", data: "estado_formatado", sortable: true },
    { title: "AÇÕES", selector: "acoes_html", data: "acoes_html", sortable: false }
  ];

  // 2. MONTAGEM DO LAYOUT VIA EMBUTIMENTO EM CRASE UTILIZANDO O NAVEGADOR SPA INTERNO
  const dadosParaTabela = filteredData.map(doc => {
    const total = doc.total_geral - (doc.total_desconto || 0);
    const pendente = total - (doc.valor_pago || 0);
    const isPendente = (doc.doc_type === "FT" || doc.doc_type === "ND") && pendente > 0.01;
    const permiteRetificacao = doc.doc_type === "FT" || doc.doc_type === "FR";

    let dropdownItens = `
      <li><button class="dropdown-item" onclick="window.handleOpenDetailsGlobal(${doc.id})">👁️ Ver Detalhes</button></li>
      <li><button class="dropdown-item" onclick="window.handleOpenGenerateGlobal(${doc.id})">📂 Gerar Documento</button></li>
    `;

    if (isPendente) {
      dropdownItens += `
        <li><hr class="dropdown-divider"></li>
        <li><button class="dropdown-item text-success fw-bold" onclick="window.handleNavigateGlobal('/faturacao/emitir-recibo/${doc.id}')">💵 Liquidar Agora</button></li>
      `;
    }

    if (permiteRetificacao) {
      dropdownItens += `
        <li><hr class="dropdown-divider"></li>
        <li><button class="dropdown-item text-danger" onclick="window.handleNavigateGlobal('/faturacao/nota-credito/${doc.id}')">⚠️ Emitir Nota Crédito</button></li>
        <li><button class="dropdown-item" onclick="window.handleNavigateGlobal('/faturacao/nota-debito/${doc.id}')">⬆️ Emitir Nota Débito</button></li>
      `;
    }

    const acoesHtml = `
      <div class="dropdown">
        <button class="btn btn-link p-0 text-muted" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          ⚙️
        </button>
        <ul class="dropdown-menu dropdown-menu-end shadow border-0">
          ${dropdownItens}
        </ul>
      </div>
    `;

    return {
      tipo_formatado: getDocLabel(doc.doc_type),
      numero: doc.numero || "",
      cliente_nome: doc.Cliente?.nome || "Consumidor Final",
      data_formatada: doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : "",
      valor_formatado: `${numberFormat(total, 2, ",", ".")} Kz`,
      estado_formatado: doc.estado,
      acoes_html: acoesHtml
    };
  });

  return (
    <div id="layout-wrapper">
      <Navbar />
      <Sidebar />
      
      <style>{`
        .documentos-page .summary-panel {
          border: 1px solid var(--fz-border);
          border-radius: 12px;
          background: var(--fz-surface);
          color: var(--fz-text);
          box-shadow: var(--fz-shadow);
        }
        .documentos-page .win10-btn { border-radius: 8px !important; transition: all 0.2s; font-size: 13px; font-weight: 700; }
        .documentos-page .win10-input { border-radius: 8px !important; border: 1px solid var(--fz-border) !important; font-size: 13px; }
        .documentos-page .filter-pill {
          cursor: pointer;
          padding: 7px 15px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          transition: 0.2s;
          border: 1px solid var(--fz-border);
          background: var(--fz-surface);
          color: var(--fz-text);
        }
        .documentos-page .filter-pill.active { background: var(--fz-primary); color: #fff; border-color: var(--fz-primary); }
        .documentos-page .doc-muted { color: var(--fz-muted) !important; }
        .documentos-page .doc-title { color: var(--fz-text) !important; }
        .datagrid-container {
          background: var(--fz-surface);
          border: 1px solid var(--fz-border);
          border-radius: 12px;
          padding: 15px;
          box-shadow: var(--fz-shadow);
        }
      `}</style>

      <main className="app-wrapper min-vh-100 documentos-page" style={{ paddingBottom: "5em" }}>
        <div className="container-fluid py-4">
          
          <div className="summary-panel p-4 mb-4">
            <div className="d-md-flex align-items-center justify-content-between mb-4">
              <div>
                <div className="d-flex align-items-center gap-2 doc-muted mb-1" style={{ fontSize: '12px' }}>
                  <span>Vendas</span> <ChevronRight size={12} /> <span className="fw-bold doc-title">Documentos Emitidos</span>
                </div>
                <h3 className="fw-bold doc-title mb-0">Gestão Comercial</h3>
              </div>
              
              <div className="d-flex gap-2 mt-3 mt-md-0">
                <InputGroup size="sm" style={{ width: '320px' }}>
                  <InputGroup.Text className="border-end-0 doc-muted"><Search size={16} /></InputGroup.Text>
                  <Form.Control 
                    className="win10-input border-start-0" 
                    placeholder="Nº Documento ou Cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
                
                {/* O botão do cabeçalho fora da tabela continua usando o Link do React Router perfeitamente */}
                <button onClick={() => navigate("/faturacao/emitir-documento/")} className="btn btn-primary win10-btn d-flex align-items-center gap-2">
                  <PlusCircle size={16} /> NOVA VENDA
                </button>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 pt-2 border-top">
              <div className={`filter-pill ${filterType === 'ALL' ? 'active' : ''}`} onClick={() => setFilterType('ALL')}>
                Todos
              </div>
              {['FT', 'FR', 'FP', 'NC', 'ND'].map(type => (
                <div key={type} className={`filter-pill ${filterType === type ? 'active' : ''}`} onClick={() => setFilterType(type)}>
                  {getDocLabel(type)}
                </div>
              ))}
            </div>
          </div>

          <div className="datagrid-container">
            <MyDataTable 
              columns={columns} 
              data={dadosParaTabela} 
              loading={loading} 
            />
          </div>

        </div>
      </main>

      {/* Modal de Detalhes */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h6 fw-bold">Detalhes do Documento: {selectedDoc?.numero}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
            <div className="row mb-4">
                <div className="col-6">
                    <small className="text-muted d-block">CLIENTE</small>
                    <span className="fw-bold">{selectedDoc?.Cliente?.nome || 'Consumidor Final'}</span>
                </div>
                <div className="col-6 text-end">
                    <small className="text-muted d-block">DATA EMISSÃO</small>
                    <span className="fw-bold">{selectedDoc && new Date(selectedDoc.createdAt).toLocaleString()}</span>
                </div>
            </div>
            <Table responsive borderless className="align-middle">
                <thead className="bg-light">
                    <tr className="small text-muted">
                        <th>PRODUTO</th>
                        <th className="text-center">QTD</th>
                        <th className="text-end">TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    {selectedDoc?.DocumentoItems?.map((item, i) => (
                        <tr key={i} className="border-bottom">
                            <td>{item.Produto?.nome}</td>
                            <td className="text-center">{item.qtd}</td>
                            <td className="text-end fw-bold">{numberFormat(item.preco_unitario * item.qtd, 2, ",", ".")} Kz</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <div className="text-end mt-3">
                <h4 className="fw-bold text-primary">{numberFormat(selectedDoc?.total_geral || 0, 2, ",", ".")} Kz</h4>
            </div>
        </Modal.Body>
      </Modal>

      {/* Modal de Sucesso */}
      {showSuccessModal && selectedDoc && (
        <FaturaSucessoModal 
          faturaId={selectedDoc.id} 
          onClose={() => setShowSuccessModal(false)} 
        />
      )}

      <Footer />
    </div>
  );
};

export default DocumentosEmitidos;